import { useEffect, useRef, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import type { ApolloError } from '@apollo/client';
import { AlertCircle } from 'lucide-react';
import { SAVE_DOCUMENT_TEXT_MUTATION } from '../graphql/mutations';
import { applyLocalSave, enqueueSave, isOfflineError, removeOutboxEntry } from '../lib/outbox';
import { Markdown } from './Markdown';
import { useWikilinkResolver } from '../hooks/useWikilinkResolver';
import { FOCUS_RING_CLASSES } from '../lib/focusTrap';

interface DocumentEditorProps {
  path: string;
  /** The stored text the edit starts from ('' when creating a new document). */
  initialContent: string;
  /**
   * `documentContent.version` for the compare-and-swap, or null when the
   * document doesn't exist yet (a create — no expectedVersion is sent).
   */
  version: number | null;
  /**
   * Opens the editor with this draft instead of `initialContent` (the
   * offline-conflict "Review your version" path: the queued offline text as
   * the draft, over the latest server text as the dirty/CAS base).
   */
  initialDraft?: string;
  /** Discard the draft and return to the rendered view. */
  onCancel: () => void;
  /** Called after a successful save (the page refetches and re-renders). */
  onSaved: () => void;
  /** Reloads the page's `documentContent` (the CONFLICT "Reload latest" path). */
  onConflictReload: () => Promise<unknown>;
}

type SaveError = 'conflict' | 'generic';

/**
 * Inline Markdown editor for the document page — replaces the old cramped
 * edit-as-text bottom sheet. Full-width Write / Preview tabs: Write is a large
 * monospace textarea over the raw text (front matter and all), Preview renders
 * the draft through the shared safe `Markdown` component. Saving sends the
 * whole blob through `saveDocumentText` with the version compare-and-swap; a
 * concurrent edit surfaces as a calm CONFLICT banner with a reload affordance,
 * never a clobber.
 */
export function DocumentEditor({
  path,
  initialContent,
  version,
  initialDraft,
  onCancel,
  onSaved,
  onConflictReload,
}: DocumentEditorProps) {
  const client = useApolloClient();
  const resolveWikilink = useWikilinkResolver();
  const [draft, setDraft] = useState(initialDraft ?? initialContent);
  const [tab, setTab] = useState<'write' | 'preview'>('write');
  const [saveError, setSaveError] = useState<SaveError | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [saveText, { loading: saving }] = useMutation(SAVE_DOCUMENT_TEXT_MUTATION, {
    refetchQueries: ['DocumentContent', 'Documents'],
    awaitRefetchQueries: true,
  });

  // Focus the textarea on open, caret at the end — ready to type.
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }, []);

  const isDirty = draft !== initialContent;

  const handleSave = async () => {
    setSaveError(null);
    try {
      await saveText({ variables: { path, content: draft, expectedVersion: version } });
      // A landed save supersedes any offline edit still queued for this path
      // (its changes were part of the base this draft was edited from).
      removeOutboxEntry(path);
      onSaved();
    } catch (err) {
      if (isOfflineError(err)) {
        // Offline, not a server response: keep the change on this device and
        // flush it when connectivity is back (see lib/outbox.ts). The cache
        // write keeps every surface showing the edit meanwhile.
        enqueueSave(path, draft, version);
        applyLocalSave(client, path, draft);
        onSaved();
        return;
      }
      const code = (err as ApolloError).graphQLErrors?.[0]?.extensions?.code;
      setSaveError(code === 'CONFLICT' ? 'conflict' : 'generic');
    }
  };

  const handleReload = async () => {
    setSaveError(null);
    await onConflictReload().catch(() => {});
    onCancel();
  };

  const tabClass = (active: boolean) =>
    `btn text-sm ${active ? 'btn-primary' : 'btn-ghost'} ${FOCUS_RING_CLASSES}`;

  return (
    <section
      className="glass-panel rounded-2xl p-5 sm:p-6 border border-[var(--card-border)] shadow-soft animate-fade-in-up"
      data-testid="document-editor"
    >
      <div className="flex items-center gap-2 mb-3" role="group" aria-label="Editor view">
        <button
          type="button"
          onClick={() => setTab('write')}
          aria-pressed={tab === 'write'}
          className={tabClass(tab === 'write')}
        >
          Write
        </button>
        <button
          type="button"
          onClick={() => setTab('preview')}
          aria-pressed={tab === 'preview'}
          className={tabClass(tab === 'preview')}
        >
          Preview
        </button>
      </div>

      {tab === 'write' ? (
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (saveError) setSaveError(null);
          }}
          aria-label="Document text"
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          autoComplete="off"
          aria-invalid={saveError ? true : undefined}
          aria-describedby={saveError ? 'document-editor-save-error' : undefined}
          onFocus={(e) => e.currentTarget.scrollIntoView({ block: 'nearest' })}
          className="w-full min-h-[50dvh] resize-y rounded-xl p-4 font-mono text-sm leading-relaxed bg-[color-mix(in_oklab,var(--text-primary)_4%,transparent)] text-[var(--text-primary)]"
        />
      ) : (
        <div className="min-h-[50dvh] rounded-xl p-4 bg-[color-mix(in_oklab,var(--text-primary)_2%,transparent)]">
          {draft.trim() ? (
            <Markdown
              content={draft}
              className="font-reading text-sm sm:text-base"
              resolveWikilink={resolveWikilink}
            />
          ) : (
            <p className="text-[var(--text-tertiary)] text-sm">Nothing to preview yet.</p>
          )}
        </div>
      )}

      {saveError && (
        <div
          id="document-editor-save-error"
          role="alert"
          className="bg-[color-mix(in_oklab,var(--coral-400)_10%,transparent)] border border-[color-mix(in_oklab,var(--coral-400)_35%,transparent)] text-[var(--text-primary)] px-4 py-3 rounded-xl text-sm mt-4"
        >
          <div className="flex gap-2 items-start">
            <AlertCircle className="w-4 h-4 text-[var(--coral-500)] shrink-0 mt-0.5" aria-hidden />
            <div>
              {saveError === 'conflict' ? (
                <>
                  <p className="font-semibold">This document changed somewhere else</p>
                  <p className="mt-1">
                    Someone (or I) edited it after you opened it, so it wasn't saved to avoid
                    overwriting that change. Copy your edits, reload the latest, then reapply them.
                  </p>
                </>
              ) : (
                <p>
                  This wasn't saved and your edits are still here. Something went wrong on our end —
                  try saving again.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-2 justify-end mt-4">
        {saveError === 'conflict' ? (
          <>
            <button type="button" onClick={onCancel} className="btn btn-ghost">
              Cancel
            </button>
            <button type="button" onClick={handleReload} className="btn btn-primary">
              Reload latest
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={onCancel} className="btn btn-ghost" disabled={saving}>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="btn btn-primary"
              disabled={saving || !isDirty}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </>
        )}
      </div>
    </section>
  );
}
